import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MagnifyingGlass,
  DownloadSimple,
  Buildings,
  MapPin,
  Briefcase,
  LinkedinLogo,
  CaretLeft,
  CaretRight,
  CircleNotch,
  CheckCircle,
  Users,
  ListBullets,
  Globe,
  ArrowLeft,
  Funnel,
  ArrowCounterClockwise,
  Envelope,
  LockOpen,
  CurrencyDollar,
  Monitor,
  CaretDown,
  CaretUp,
  Plus,
  Tag as TagIcon,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '../../../components/ui/Button';
import { useContactStore } from '../../../stores/contactStore';
import { integrationsApi } from '../../../lib/api';
import { useScrollLock } from '../../../lib/useScrollLock';
import { toast } from 'sonner';
import { SENIORITY_OPTIONS } from '../../../lib/seniority';
import apolloIcon from '@/assets/apollo-icon.jpeg';

interface ApolloSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ApolloOrg {
  id: string;
  name: string;
  website_url?: string;
  industry?: string;
  estimated_num_employees?: number;
  logo_url?: string;
  city?: string;
  state?: string;
  country?: string;
}

interface ApolloList {
  id: string;
  name: string;
  created_at?: string;
  cached_count?: number;
}

const EMPLOYEE_RANGE_OPTIONS = [
  { value: '1,10', label: '1-10' },
  { value: '11,50', label: '11-50' },
  { value: '51,200', label: '51-200' },
  { value: '201,500', label: '201-500' },
  { value: '501,1000', label: '501-1K' },
  { value: '1001,5000', label: '1K-5K' },
  { value: '5001,10000', label: '5K-10K' },
  { value: '10001,', label: '10K+' },
];

const EMAIL_STATUS_OPTIONS = [
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'likely to engage', label: 'Likely to Engage' },
];


type TabType = 'search' | 'lists';

// Why cancelling a search can end up unverified: the backend tracks in-flight
// `fetch_all` sweeps in a per-process dict, but the API runs several worker
// processes. A cancel request that lands on a worker other than the one running
// the sweep answers "no such search" while the sweep carries on — and carries on
// spending Apollo credits. So a cancel has three possible outcomes and only one
// of them lets us claim the search actually stopped.
type UnverifiedCancel = 'not-acknowledged' | 'unknown';

// Shared by the toast and the inline note so the two can't drift apart. Both
// state only what we know — the browser stopped waiting — and name the credit
// exposure, since that's the part that costs the user money.
const UNVERIFIED_CANCEL_COPY: Record<UnverifiedCancel, string> = {
  'not-acknowledged':
    'Stopped waiting for results. Apollo may still be running the search on the server and consuming credits.',
  unknown:
    'Stopped waiting for results — the cancellation could not be confirmed. Apollo may still be running the search on the server and consuming credits.',
};

// Shared field tokens — Editorial Dark + Light dual-mode
const FIELD_LABEL =
  'block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]';

const FIELD_INPUT =
  'w-full px-3 py-2 rounded-md border bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50 transition-colors duration-100 hover:border-border/80 focus:outline-none focus:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:hover:border-white/[0.14] dark:focus:border-[#7170ff]/75';

const FIELD_INPUT_WITH_ICON = `${FIELD_INPUT} pl-8`;

const FIELD_ICON =
  'absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98] pointer-events-none';

// Pill chip — semantic
const PILL_BASE = 'inline-flex items-center px-2 py-0.5 rounded-full border transition-colors duration-100';
const PILL_ACTIVE = 'bg-[#5e6ad2] text-white border-[#5e6ad2]';
const PILL_INACTIVE =
  'bg-muted/40 text-muted-foreground border-border/50 hover:bg-muted/70 hover:text-foreground dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] dark:hover:bg-white/[0.06] dark:hover:text-[#f7f8f8]';
const PILL_TEXT_STYLE = { fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' } as const;

// ─── Autocomplete dropdown component ────────────────────────────────────────
function CompanyAutocomplete({
  selectedCompanies,
  onAdd,
  onRemove,
}: {
  selectedCompanies: ApolloOrg[];
  onAdd: (org: ApolloOrg) => void;
  onRemove: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ApolloOrg[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    try {
      const res = await integrationsApi.apolloSearchOrganizations({ query: q, per_page: 10 });
      setResults(res.data.organizations || []);
      setSearched(true);
      setOpen(true);
    } catch {
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 350);
  };

  return (
    <div className="relative">
      <label className={FIELD_LABEL}>Companies</label>

      {/* Selected chips */}
      {selectedCompanies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedCompanies.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-md bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/25 dark:text-[#a5b4fc]"
              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              {c.logo_url && <img src={c.logo_url} alt="" className="w-3.5 h-3.5 rounded object-cover" />}
              <span className="truncate max-w-[160px]">{c.name}</span>
              <button
                onClick={() => onRemove(c.id)}
                className="p-0.5 rounded hover:bg-[#5e6ad2]/15 dark:hover:bg-[#5e6ad2]/20 transition-colors"
                aria-label={`Remove ${c.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        <Buildings className={FIELD_ICON} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Type to search companies…"
          className={FIELD_INPUT_WITH_ICON + ' pr-9'}
        />
        {loading && (
          <CircleNotch className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground dark:text-[#8a8f98]" />
        )}
      </div>

      {/* Dropdown */}
      {open && searched && results.length === 0 && !loading && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md p-4 text-center bg-card border border-border/60 shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]"
        >
          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510 }}>
            No companies found for "{query}"
          </p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={{ fontSize: '11px', letterSpacing: '-0.1px' }}>
            Try a shorter or different search term.
          </p>
        </div>
      )}
      {open && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md max-h-72 overflow-y-auto bg-card border border-border/60 shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]"
        >
          {results.map((org) => {
            const selected = selectedCompanies.some((c) => c.id === org.id);
            return (
              <button
                key={org.id}
                onClick={() => { onAdd(org); setQuery(''); setOpen(false); }}
                disabled={selected}
                className={`w-full px-3 py-2.5 text-left flex items-center gap-3 border-b border-border/50 dark:border-white/[0.05] last:border-0 transition-colors duration-100 ${
                  selected
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]'
                }`}
              >
                {org.logo_url ? (
                  <img src={org.logo_url} alt="" className="w-8 h-8 rounded-md object-cover shrink-0 ring-1 ring-border/60 dark:ring-white/[0.06]" />
                ) : (
                  <div className="w-8 h-8 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center shrink-0">
                    <Buildings className="w-4 h-4 text-muted-foreground dark:text-[#8a8f98]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    {org.name}
                  </div>
                  <div
                    className="text-muted-foreground dark:text-[#8a8f98] truncate"
                    style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                  >
                    {[
                      org.industry,
                      org.estimated_num_employees ? `${org.estimated_num_employees.toLocaleString()} employees` : null,
                      [org.city, org.country].filter(Boolean).join(', '),
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {selected && <CheckCircle className="h-4 w-4 text-emerald-500 dark:text-emerald-400 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export function ApolloSearchModal({ isOpen, onClose }: ApolloSearchModalProps) {
  useScrollLock(isOpen);
  const {
    apolloResults, apolloLoading,
    apolloSearch, cancelApolloSearch, apolloImport, clearApolloResults,
    contactLists, fetchContactLists, createContactList,
    allTags, fetchTags,
  } = useContactStore();

  const [activeTab, setActiveTab] = useState<TabType>('search');

  // Destination list — when set, imported contacts are appended to this list
  const [destinationListId, setDestinationListId] = useState<number | null>(null);
  const [showListsPanel, setShowListsPanel] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);

  // Search filters
  const [query, setQuery] = useState('');
  const [titles, setTitles] = useState('');
  const [includeSimilarTitles, setIncludeSimilarTitles] = useState(true);
  const [locations, setLocations] = useState('');
  const [orgLocations, setOrgLocations] = useState('');
  const [seniority, setSeniority] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<ApolloOrg[]>([]);
  const [companyDomains, setCompanyDomains] = useState('');
  const [employeeRanges, setEmployeeRanges] = useState<string[]>([]);
  const [revenueMin, setRevenueMin] = useState('');
  const [revenueMax, setRevenueMax] = useState('');
  const [technologies, setTechnologies] = useState('');
  const [emailStatus, setEmailStatus] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Saved-lists tab still pages server-side, 100 rows (Apollo's max) per request.
  // The search tab instead uses fetch_all — the backend sweeps every Apollo page
  // and returns the entire result set in one batch; the client only paginates
  // it into 25-row visual pages (see DISPLAY_PAGE_SIZE below).
  const perPage = 100;

  // Saved lists
  const [apolloLists, setApolloLists] = useState<ApolloList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listsError, setListsError] = useState<string | null>(null);
  const [selectedList, setSelectedList] = useState<ApolloList | null>(null);
  const [listContacts, setListContacts] = useState<any[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);

  // Selection & import
  const [selectedPeople, setSelectedPeople] = useState<Set<number>>(new Set());
  // Tag picker — multi-select. Empty array means "no tags applied to imported contacts."
  const [selectedTags, setSelectedTags] = useState<string[]>(['apollo-import']);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [importing, setImporting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch saved lists
  useEffect(() => {
    if (activeTab === 'lists' && apolloLists.length === 0 && !listsError) {
      fetchLists();
    }
  }, [activeTab]);

  // Pull local contact lists when the modal opens so the destination picker is ready
  useEffect(() => {
    if (isOpen) fetchContactLists();
  }, [isOpen, fetchContactLists]);

  // Pull existing user tags when the modal opens so the tag picker can offer them
  useEffect(() => {
    if (isOpen) fetchTags();
  }, [isOpen, fetchTags]);

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (!name) { toast.error('List name is required'); return; }
    if (selectedPeople.size === 0) { toast.error('Select at least one contact first'); return; }
    // Every selected contact must already have a revealed email — we never want a
    // partial add-to-list where unrevealed rows are silently dropped on the floor.
    const sourcePeople = activeTab === 'search' ? apolloResults : listContacts;
    const missingEmails = Array.from(selectedPeople).filter(
      (idx) => !(sourcePeople[idx] as any)?.email
    ).length;
    if (missingEmails > 0) {
      toast.error(
        `Reveal emails on the remaining ${missingEmails} selected contact${missingEmails === 1 ? '' : 's'} before creating a list.`
      );
      return;
    }
    setCreatingList(true);
    try {
      // If a list with this name already exists (case-insensitive), the backend
      // returns that list instead of creating a duplicate.
      const reused = contactLists.some((l) => l.name.trim().toLowerCase() === name.toLowerCase());
      const created = await createContactList({ name, contact_ids: [] });
      setDestinationListId(created.id);
      setNewListName('');
      setShowListsPanel(false);
      toast.success(reused ? `Using existing list "${created.name}"` : `Created list "${created.name}"`);
      // Save the currently-selected Apollo contacts straight into the new list.
      // Pass the id explicitly so we don't race React's pending setDestinationListId.
      await handleImport(created.id);
    } catch {
      toast.error('Failed to create list');
    } finally {
      setCreatingList(false);
    }
  };

  const destinationList = destinationListId
    ? contactLists.find(l => l.id === destinationListId) ?? null
    : null;

  const fetchLists = async () => {
    setListsLoading(true); setListsError(null);
    try {
      const res = await integrationsApi.apolloGetLists();
      setApolloLists(res.data.lists || []);
    } catch (error: any) {
      setListsError(error.response?.data?.detail || 'Failed to fetch Apollo lists');
    } finally {
      setListsLoading(false);
    }
  };

  // Per-row reveal — bulk_match costs Apollo credits per person, so this is an
  // explicit user action on a single row rather than something we auto-fire.
  // The set drives a spinner on just the row being revealed.
  const [revealingIds, setRevealingIds] = useState<Set<string>>(new Set());

  // Flag rows whose reveal came back empty so the table shows a greyed-out
  // "No email" instead of offering the reveal button (and burning credits) again.
  const markEmailNotFound = useCallback((personIds: string[]) => {
    const idSet = new Set(personIds);
    const flag = (p: any) =>
      p?.id && idSet.has(p.id) && !p.email ? { ...p, email_not_found: true } : p;
    const apolloResultsNow = useContactStore.getState().apolloResults;
    if (apolloResultsNow.some((p: any) => p?.id && idSet.has(p.id))) {
      useContactStore.setState({ apolloResults: apolloResultsNow.map(flag) });
    }
    setListContacts(prev => prev.map(flag));
  }, []);

  const handleRevealSingle = useCallback(async (personId: string) => {
    if (!personId) return;
    // Use a functional set update + guard so back-to-back clicks on different
    // rows can't be skipped by a stale closure read.
    let alreadyRunning = false;
    setRevealingIds(prev => {
      if (prev.has(personId)) { alreadyRunning = true; return prev; }
      const n = new Set(prev); n.add(personId); return n;
    });
    if (alreadyRunning) return;
    try {
      const res = await integrationsApi.apolloBulkEnrich([personId]);
      const enriched = res.data.enriched || [];
      // Backend may include a soft-error message (no match, identity mismatch,
      // timeout) alongside success=true and enriched=[].
      const softError = (res.data as any)?.error as string | undefined;
      // We sent exactly one id, so the first (and only) match is ours.
      // Don't `.find()` by id — Apollo can return a canonical id that doesn't
      // equal the input id, which would silently drop the result.
      const enrichedPerson = enriched[0];
      if (!enrichedPerson) {
        markEmailNotFound([personId]);
        toast.warning(softError || 'No email found for this person!');
        return;
      }

      // Merge into whichever data source the row came from. Always key the
      // merge by the input personId — that's the id the row was rendered with.
      const apolloResultsNow = useContactStore.getState().apolloResults;
      if (apolloResultsNow.some((p: any) => p?.id === personId)) {
        useContactStore.setState({
          apolloResults: apolloResultsNow.map((p: any) =>
            p?.id === personId ? { ...p, ...enrichedPerson, id: p.id } : p
          ),
        });
      }
      setListContacts(prev => prev.map((p: any) =>
        p?.id === personId ? { ...p, ...enrichedPerson, id: p.id } : p
      ));

      if (enrichedPerson.email) {
        toast.success(`Revealed email (${res.data.credits_consumed} credit${res.data.credits_consumed === 1 ? '' : 's'} used)`);
      } else {
        markEmailNotFound([personId]);
        toast.warning('No email found for this person!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reveal email');
    } finally {
      setRevealingIds(prev => { const n = new Set(prev); n.delete(personId); return n; });
    }
  }, [markEmailNotFound]);

  const loadListContacts = async (list: ApolloList, page = 1) => {
    setSelectedList(list); setListLoading(true); setSelectedPeople(new Set());
    setListDisplayPage(1);
    try {
      // Pull the ENTIRE list — the server walks every Apollo page (fetch_all)
      // instead of returning just the first 100. Held fully in memory like the
      // search tab, so paging is in-memory and select-all / import span all
      // contacts rather than a single 100-row batch.
      const res = await integrationsApi.apolloGetListContacts(list.id, page, perPage, true);
      setListContacts(res.data.contacts || []);
      setListTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load list contacts');
    } finally {
      setListLoading(false);
    }
  };

  // People search — fetch_all pulls every matching contact Apollo will serve
  // (100 per request server-side, up to Apollo's 50k-per-search ceiling).
  const handleSearch = async () => {
    // A fresh search supersedes any earlier unverified-cancel note.
    setUnverifiedCancel(null);
    const filters: any = { fetch_all: true };
    if (query.trim()) filters.query = query.trim();
    if (titles.trim()) filters.titles = titles.split(',').map((t: string) => t.trim()).filter(Boolean);
    if (!includeSimilarTitles) filters.include_similar_titles = false;
    if (locations.trim()) filters.locations = locations.split(',').map((l: string) => l.trim()).filter(Boolean);
    if (orgLocations.trim()) filters.organization_locations = orgLocations.split(',').map((l: string) => l.trim()).filter(Boolean);
    if (seniority.length > 0) filters.seniority_levels = seniority;
    if (selectedCompanies.length > 0) {
      filters.organization_ids = selectedCompanies.map((c) => c.id);
    }
    if (companyDomains.trim()) filters.organization_domains = companyDomains.split(',').map((d: string) => d.trim()).filter(Boolean);
    if (employeeRanges.length > 0) filters.employee_ranges = employeeRanges;
    if (revenueMin.trim()) filters.revenue_min = parseInt(revenueMin.replace(/[^0-9]/g, ''));
    if (revenueMax.trim()) filters.revenue_max = parseInt(revenueMax.replace(/[^0-9]/g, ''));
    if (technologies.trim()) filters.technologies = technologies.split(',').map((t: string) => t.trim().toLowerCase().replace(/ /g, '_').replace(/\./g, '_')).filter(Boolean);
    if (emailStatus.length > 0) filters.email_status = emailStatus;
    try {
      await apolloSearch(filters);
      setHasSearched(true);
      setSelectedPeople(new Set());
    } catch (error: any) {
      // A user-initiated cancel surfaces as an axios abort — the cancel
      // handler already shows its own toast, so don't report a failure.
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
      toast.error('Apollo search failed. Check your API key in Settings > Integrations.');
    }
  };

  // Cancelling is only half in our hands. Dropping the browser's pending
  // request is local and always works; stopping the server-side sweep is the
  // backend's call and it only sometimes reports back that it did (see
  // UnverifiedCancel above). So we claim the search stopped only when the
  // backend confirms it, and otherwise tell the user plainly that Apollo may
  // still be working — and billing — on the server. `canceling` holds the
  // button in "Canceling…" until that reply lands, then it reverts.
  const [canceling, setCanceling] = useState(false);
  const [unverifiedCancel, setUnverifiedCancel] = useState<UnverifiedCancel | null>(null);
  const handleCancelSearch = async () => {
    if (canceling) return;
    setCanceling(true);
    try {
      const { aborted, serverConfirmed } = await cancelApolloSearch();
      // Nothing was in flight, so no request was dropped and there's nothing
      // to report either way.
      if (!aborted) return;
      if (serverConfirmed === true) {
        setUnverifiedCancel(null);
        toast.info('Request canceled!');
        return;
      }
      // serverConfirmed === false → the backend had no record of the sweep.
      // serverConfirmed === null  → the cancel request itself failed.
      const outcome: UnverifiedCancel = serverConfirmed === false ? 'not-acknowledged' : 'unknown';
      setUnverifiedCancel(outcome);
      toast.warning(UNVERIFIED_CANCEL_COPY[outcome]);
    } finally {
      setCanceling(false);
    }
  };

  const resetFilters = () => {
    setQuery(''); setTitles(''); setIncludeSimilarTitles(true);
    setLocations(''); setOrgLocations('');
    setSeniority([]); setSelectedCompanies([]);
    setCompanyDomains(''); setEmployeeRanges([]);
    setRevenueMin(''); setRevenueMax('');
    setTechnologies(''); setEmailStatus([]);
  };

  // Displayed data
  const currentPeople = activeTab === 'search' ? apolloResults : listContacts;
  const currentLoading = activeTab === 'search' ? apolloLoading : listLoading;
  // Both tabs load their full result set in one shot (search + list both use
  // fetch_all), so all pagination is over in-memory 25-row display windows.

  // ── Client-side pagination ─────────────────────────────────────────────────
  // The full result set (search or list) is already in memory via fetch_all, so
  // we just render 25 rows at a time by slicing it — every window is a client
  // slice, no server re-fetch.
  const DISPLAY_PAGE_SIZE = 25;
  const [searchDisplayPage, setSearchDisplayPage] = useState(1);
  const [listDisplayPage, setListDisplayPage] = useState(1);
  const displayPage = activeTab === 'search' ? searchDisplayPage : listDisplayPage;
  const setDisplayPage = activeTab === 'search' ? setSearchDisplayPage : setListDisplayPage;
  const displayOffset = (displayPage - 1) * DISPLAY_PAGE_SIZE;
  const displayedPeople = currentPeople.slice(displayOffset, displayOffset + DISPLAY_PAGE_SIZE);
  const displayPageCount = Math.max(1, Math.ceil(currentPeople.length / DISPLAY_PAGE_SIZE));
  // 1-based row range over the full in-memory set — "Showing 1–25 of 3,530",
  // "26–50 of 3,530", etc.
  const globalRowStart = currentPeople.length === 0 ? 0 : displayOffset + 1;
  const globalRowEnd = displayOffset + displayedPeople.length;
  // Are we at the very first/last 25-row window?
  const atFirstWindow = displayPage <= 1;
  const atLastWindow = displayPage >= displayPageCount;

  // Denominator for the "Showing X–Y of N" text — the full loaded set. Apollo's
  // reported `total_entries` is unreliable (sometimes 0 even when rows came
  // back), so we count the rows actually returned.
  const effectiveTotal = currentPeople.length;

  // Only selected contacts with a revealed email can be added to a list / imported.
  // We compute this once here so the action-bar and the destination-picker stay in sync.
  const selectedWithEmailCount = Array.from(selectedPeople).filter(
    (idx) => (currentPeople[idx] as any)?.email
  ).length;
  const selectedWithoutEmailCount = selectedPeople.size - selectedWithEmailCount;

  // Reveal emails for selected people via bulk enrich
  const handleRevealEmails = async () => {
    if (selectedPeople.size === 0) { toast.error('Select at least one contact'); return; }
    setEnriching(true);
    try {
      const selectedIds = currentPeople
        .filter((_: any, idx: number) => selectedPeople.has(idx))
        .map((p: any) => p.id)
        .filter(Boolean);

      if (selectedIds.length === 0) {
        toast.error('No valid person IDs to enrich');
        return;
      }

      const res = await integrationsApi.apolloBulkEnrich(selectedIds);
      const enriched = res.data.enriched || [];

      // Build a map of id -> enriched person for quick lookup
      const enrichMap = new Map<string, any>();
      for (const person of enriched) {
        if (person?.id) enrichMap.set(person.id, person);
      }

      // Update results in-place
      if (activeTab === 'search') {
        const updated = apolloResults.map((p: any) => {
          const enrichedPerson = enrichMap.get(p.id);
          if (enrichedPerson) {
            return { ...p, ...enrichedPerson };
          }
          return p;
        });
        // Use the store's internal setter — update via apolloSearch result shape
        useContactStore.setState({
          apolloResults: updated,
        });
      } else {
        const updated = listContacts.map((p: any) => {
          const enrichedPerson = enrichMap.get(p.id);
          if (enrichedPerson) {
            return { ...p, ...enrichedPerson };
          }
          return p;
        });
        setListContacts(updated);
      }

      // Any selected row still missing an email after the merge gets flagged so
      // its "Click to reveal" turns into a greyed-out "No email".
      markEmailNotFound(selectedIds);

      const emailCount = enriched.filter((p: any) => p.email).length;
      if (emailCount === 0) {
        toast.warning(selectedIds.length === 1 ? 'No email found for this person!' : 'No email found for these people!');
      } else {
        toast.success(`Revealed ${emailCount} email${emailCount !== 1 ? 's' : ''} (${res.data.credits_consumed} credits used)`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reveal emails');
    } finally {
      setEnriching(false);
    }
  };

  const handleImport = async (overrideListId?: number) => {
    if (selectedPeople.size === 0) { toast.error('Select at least one contact'); return; }
    // Guard: a React onClick handler will pass a SyntheticEvent as the first arg.
    // Only accept a real numeric list id, otherwise fall through to destinationListId.
    const passedListId = typeof overrideListId === 'number' ? overrideListId : undefined;
    const effectiveListId = passedListId ?? destinationListId ?? undefined;

    // Only contacts whose emails have already been revealed can be added — without
    // an email there's no way to reach them or run a campaign, so they're excluded.
    const peopleToImport = currentPeople.filter((_: any, idx: number) => selectedPeople.has(idx));
    const withEmail = peopleToImport.filter((p: any) => p.email);
    const skippedCount = peopleToImport.length - withEmail.length;

    if (withEmail.length === 0) {
      toast.error('Reveal emails before adding contacts.');
      return;
    }
    if (skippedCount > 0) {
      toast.info(`Skipping ${skippedCount} contact${skippedCount === 1 ? '' : 's'} without a revealed email.`);
    }

    setImporting(true);
    try {
      const people = withEmail
        .map((p: any) => ({
          email: p.email,
          first_name: p.first_name,
          last_name: p.last_name,
          title: p.title,
          seniority: p.seniority,
          linkedin_url: p.linkedin_url,
          twitter_url: p.twitter_url,
          city: p.city,
          state: p.state,
          country: p.country,
          phone_number: p.phone_numbers?.[0]?.sanitized_number || null,
          organization_name: p.organization?.name || p.organization_name,
          organization_website: p.organization?.website_url || p.organization_website_url,
          organization_industry: p.organization?.industry,
          organization_size: p.organization?.estimated_num_employees ? String(p.organization.estimated_num_employees) : null,
          departments: p.departments || [],
        }));
      const result = await apolloImport(people, selectedTags.length > 0 ? selectedTags : undefined, effectiveListId);
      const matchedList = effectiveListId !== undefined
        ? (contactLists.find(l => l.id === effectiveListId) ?? null)
        : null;
      const listSuffix = matchedList ? ` → "${matchedList.name}"` : '';
      toast.success(`Imported ${result.imported} contacts${listSuffix}${result.duplicates > 0 ? ` (${result.duplicates} duplicates skipped)` : ''}`);
      setSelectedPeople(new Set());
    } catch {
      toast.error('Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  // Select-all toggles only the rows visible on the current display page —
  // selections on other pages of the same fetch are retained so you can multi-page select.
  const toggleSelectAll = () => {
    const visibleIndices = displayedPeople.map((_, i) => i + displayOffset);
    const allVisibleSelected =
      visibleIndices.length > 0 && visibleIndices.every((i) => selectedPeople.has(i));
    const next = new Set(selectedPeople);
    if (allVisibleSelected) {
      for (const i of visibleIndices) next.delete(i);
    } else {
      for (const i of visibleIndices) next.add(i);
    }
    setSelectedPeople(next);
  };

  const toggleSelect = (idx: number) => {
    const s = new Set(selectedPeople);
    if (s.has(idx)) s.delete(idx); else s.add(idx);
    setSelectedPeople(s);
  };

  // Select every fetched record, not just the current 25-row display page.
  // Search results are fully in memory (fetch_all), so this covers the entire
  // searched set; on the lists tab it covers the loaded batch (what the footer
  // count reflects). Backs the "Select all N results" banner affordance.
  const selectAllResults = () => {
    setSelectedPeople(new Set(currentPeople.map((_: any, i: number) => i)));
  };

  const clearSelection = () => setSelectedPeople(new Set());

  // Combined Prev/Next: walks the 25-row display windows. Both tabs hold their
  // full result set in memory (search + list use fetch_all), so paging never
  // re-fetches.
  const goToPrevPage = () => {
    if (displayPage > 1) setDisplayPage(displayPage - 1);
  };

  const goToNextPage = () => {
    if (displayPage < displayPageCount) setDisplayPage(displayPage + 1);
  };

  const handleClose = () => {
    // Best-effort ask for the server-side sweep to stop, so it isn't left
    // running after the modal is gone. Fire-and-forget on purpose: the outcome
    // is unverifiable (see UnverifiedCancel) and there's no UI left to report it
    // to, so unlike handleCancelSearch we don't await or surface anything.
    cancelApolloSearch();
    clearApolloResults(); setHasSearched(false); setSelectedPeople(new Set());
    setUnverifiedCancel(null);
    resetFilters(); setSelectedList(null); setListContacts([]); setActiveTab('search');
    setDestinationListId(null); setShowListsPanel(false); setNewListName('');
    setSelectedTags(['apollo-import']); setShowTagsPanel(false); setNewTagName('');
    setSearchDisplayPage(1); setListDisplayPage(1);
    onClose();
  };

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const hasActiveFilters = query || titles || locations || orgLocations || seniority.length > 0 || selectedCompanies.length > 0 || companyDomains || employeeRanges.length > 0 || revenueMin || revenueMax || technologies || emailStatus.length > 0;

  return createPortal((
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="bg-background dark:bg-[#0f1011] flex flex-col"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 200,
          margin: 0,
        }}
      >
        {/* ── Top bar ── */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border/50 dark:border-white/[0.06] shrink-0 bg-card dark:bg-[#191a1b]">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={handleClose}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
              aria-label="Close"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={apolloIcon}
                alt=""
                className="h-9 w-9 rounded-md ring-1 ring-border/60 dark:ring-white/[0.08] object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <h1
                  className="text-foreground dark:text-[#f7f8f8] truncate"
                  style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                >
                  Apollo.io
                </h1>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] truncate"
                  style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.4 }}
                >
                  Search 275M+ contacts and companies
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Destination list picker — imported Apollo contacts get appended to this list.
                Gated on: Apollo search results loaded AND at least one contact selected. */}
            {(() => {
              const missingEmailCount = selectedPeople.size - selectedWithEmailCount;
              const listPickerEnabled = hasSearched && selectedPeople.size > 0 && missingEmailCount === 0;
              const listPickerHint = !hasSearched
                ? 'Run an Apollo search first'
                : selectedPeople.size === 0
                  ? 'Select contacts from the results to enable'
                  : missingEmailCount > 0
                    ? `Reveal emails on the remaining ${missingEmailCount} selected contact${missingEmailCount === 1 ? '' : 's'} first`
                    : undefined;
              return (
            <div className="relative">
              <button
                onClick={() => { if (listPickerEnabled) setShowListsPanel(v => !v); }}
                disabled={!listPickerEnabled}
                title={listPickerHint}
                aria-haspopup="menu"
                aria-expanded={showListsPanel}
                aria-disabled={!listPickerEnabled}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
                  destinationList
                    ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.08] text-foreground dark:border-[#7170ff]/40 dark:bg-[#7170ff]/[0.10] dark:text-[#f7f8f8]'
                    : 'border-border/60 bg-background text-foreground enabled:hover:bg-muted/40 dark:border-white/[0.10] dark:bg-white/[0.02] dark:text-[#f7f8f8] dark:enabled:hover:bg-white/[0.05]'
                }`}
                style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <ListBullets className={`h-3.5 w-3.5 ${destinationList ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-muted-foreground dark:text-[#8a8f98]'}`} />
                <span className="truncate max-w-[160px]">
                  {destinationList ? destinationList.name : 'Add to list'}
                </span>
                {!destinationList && contactLists.length > 0 && (
                  <span
                    className="ml-0.5 h-4 min-w-[1rem] px-1 rounded-full bg-muted text-muted-foreground dark:bg-white/[0.06] dark:text-[#d0d6e0] inline-flex items-center justify-center tabular-nums"
                    style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {contactLists.length}
                  </span>
                )}
                <CaretDown className="h-3 w-3 opacity-60" />
              </button>

              {showListsPanel && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowListsPanel(false)} />
                  <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg z-50 overflow-hidden bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20 dark:border-white/[0.06] dark:bg-white/[0.02]">
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        Import destination
                      </h3>
                      <button
                        onClick={() => setShowListsPanel(false)}
                        className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-100"
                        aria-label="Close lists"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Create new list */}
                    <div className="px-4 py-3 border-b border-border/50 dark:border-white/[0.06]">
                      <label
                        className="block mb-1.5 text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                      >
                        Create new list
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newListName}
                          onChange={(e) => setNewListName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !creatingList) { e.preventDefault(); handleCreateList(); } }}
                          placeholder="e.g. Q3 healthcare leads"
                          className="flex-1 px-2.5 py-1.5 rounded-md border bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:focus:border-[#7170ff]/75"
                          style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
                        />
                        <button
                          onClick={handleCreateList}
                          disabled={
                            creatingList ||
                            !newListName.trim() ||
                            selectedPeople.size === 0 ||
                            selectedWithEmailCount < selectedPeople.size
                          }
                          title={
                            selectedPeople.size === 0
                              ? 'Select at least one contact first'
                              : selectedWithEmailCount < selectedPeople.size
                                ? `Reveal emails on the remaining ${selectedPeople.size - selectedWithEmailCount} selected contact${selectedPeople.size - selectedWithEmailCount === 1 ? '' : 's'} first`
                                : undefined
                          }
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#5e6ad2] text-white hover:bg-[#5e6ad2]/90 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[#7170ff] dark:hover:bg-[#7170ff]/90 transition-colors"
                          style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          {creatingList ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          Create
                        </button>
                      </div>
                    </div>

                    {/* Existing lists */}
                    {contactLists.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                          <ListBullets className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
                        </div>
                        <p
                          className="text-foreground dark:text-[#f7f8f8] mb-1"
                          style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          No lists yet
                        </p>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed max-w-[15rem] mx-auto"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Create one above to send imported Apollo contacts straight into it.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-72 overflow-y-auto divide-y divide-border/30 dark:divide-white/[0.04]">
                        <button
                          onClick={() => { setDestinationListId(null); setShowListsPanel(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100 ${
                            destinationListId === null
                              ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]'
                              : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]'
                          }`}
                        >
                          <div className="h-7 w-7 rounded-md bg-muted/70 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                            <X className="h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-foreground dark:text-[#f7f8f8]"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                            >
                              Don't add to a list
                            </p>
                            <p
                              className="text-muted-foreground dark:text-[#8a8f98]"
                              style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                            >
                              Just import as regular contacts
                            </p>
                          </div>
                          {destinationListId === null && (
                            <CheckCircle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" />
                          )}
                        </button>
                        {contactLists.map(list => {
                          const active = destinationListId === list.id;
                          return (
                            <button
                              key={list.id}
                              onClick={() => { setDestinationListId(list.id); setShowListsPanel(false); }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100 ${
                                active
                                  ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]'
                                  : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]'
                              }`}
                            >
                              <div className="h-7 w-7 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center flex-shrink-0">
                                <ListBullets className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p
                                  className="text-foreground dark:text-[#f7f8f8] truncate"
                                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                                >
                                  {list.name}
                                </p>
                                <p
                                  className="text-muted-foreground dark:text-[#8a8f98]"
                                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                                >
                                  {list.total_contacts} contacts
                                  {list.description && ` · ${list.description}`}
                                </p>
                              </div>
                              {active && (
                                <CheckCircle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
              );
            })()}

            {/* Tag picker — multi-select. Tags apply to every contact imported
                from this modal, independent of whether a destination list is set. */}
            <div className="relative">
              <button
                onClick={() => setShowTagsPanel(v => !v)}
                aria-haspopup="menu"
                aria-expanded={showTagsPanel}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border transition-colors duration-150 ${
                  selectedTags.length > 0
                    ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.08] text-foreground dark:border-[#7170ff]/40 dark:bg-[#7170ff]/[0.10] dark:text-[#f7f8f8]'
                    : 'border-border/60 bg-background text-foreground hover:bg-muted/40 dark:border-white/[0.10] dark:bg-white/[0.02] dark:text-[#f7f8f8] dark:hover:bg-white/[0.05]'
                }`}
                style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <TagIcon className={`h-3.5 w-3.5 ${selectedTags.length > 0 ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-muted-foreground dark:text-[#8a8f98]'}`} />
                <span className="truncate max-w-[160px]">
                  {selectedTags.length === 0
                    ? 'Add tags'
                    : selectedTags.length === 1
                      ? selectedTags[0]
                      : `${selectedTags.length} tags`}
                </span>
                {selectedTags.length > 1 && (
                  <span
                    className="ml-0.5 h-4 min-w-[1rem] px-1 rounded-full bg-[#5e6ad2]/15 text-[#5e6ad2] dark:bg-[#7170ff]/[0.18] dark:text-[#a5b4fc] inline-flex items-center justify-center tabular-nums"
                    style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {selectedTags.length}
                  </span>
                )}
                <CaretDown className="h-3 w-3 opacity-60" />
              </button>

              {showTagsPanel && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowTagsPanel(false)} />
                  <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg z-50 overflow-hidden bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20 dark:border-white/[0.06] dark:bg-white/[0.02]">
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        Tags to apply
                      </h3>
                      <button
                        onClick={() => setShowTagsPanel(false)}
                        className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-100"
                        aria-label="Close tags"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Create new tag */}
                    <div className="px-4 py-3 border-b border-border/50 dark:border-white/[0.06]">
                      <label
                        className="block mb-1.5 text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                      >
                        Create new tag
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            const name = newTagName.trim();
                            if (!name) return;
                            if (!selectedTags.includes(name)) setSelectedTags(prev => [...prev, name]);
                            setNewTagName('');
                          }}
                          placeholder="e.g. q3-healthcare-leads"
                          className="flex-1 px-2.5 py-1.5 rounded-md border bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:focus:border-[#7170ff]/75"
                          style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
                        />
                        <button
                          onClick={() => {
                            const name = newTagName.trim();
                            if (!name) return;
                            if (!selectedTags.includes(name)) setSelectedTags(prev => [...prev, name]);
                            setNewTagName('');
                          }}
                          disabled={!newTagName.trim()}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#5e6ad2] text-white hover:bg-[#5e6ad2]/90 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[#7170ff] dark:hover:bg-[#7170ff]/90 transition-colors"
                          style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Selected tag chips (custom or pre-existing) — shows everything that
                        will be applied on import, including tags the user typed that aren't
                        in the saved list yet. */}
                    {selectedTags.length > 0 && (
                      <div className="px-4 py-2.5 border-b border-border/50 dark:border-white/[0.06] bg-muted/10 dark:bg-white/[0.015]">
                        <div className="flex flex-wrap gap-1.5">
                          {selectedTags.map(t => (
                            <span
                              key={t}
                              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/25 dark:text-[#a5b4fc]"
                              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                            >
                              <span className="truncate max-w-[140px]">{t}</span>
                              <button
                                onClick={() => setSelectedTags(prev => prev.filter(x => x !== t))}
                                className="p-0.5 rounded hover:bg-[#5e6ad2]/15 dark:hover:bg-[#5e6ad2]/20 transition-colors"
                                aria-label={`Remove tag ${t}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                          {selectedTags.length > 1 && (
                            <button
                              onClick={() => setSelectedTags([])}
                              className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors"
                              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Existing tags — multi-toggle */}
                    {allTags.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                          <TagIcon className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
                        </div>
                        <p
                          className="text-foreground dark:text-[#f7f8f8] mb-1"
                          style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          No saved tags
                        </p>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed max-w-[15rem] mx-auto"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Create one above — it'll apply to every contact imported from this Apollo session.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-72 overflow-y-auto divide-y divide-border/30 dark:divide-white/[0.04]">
                        {allTags.map(tag => {
                          const active = selectedTags.includes(tag.name);
                          return (
                            <button
                              key={tag.name}
                              onClick={() => setSelectedTags(prev =>
                                prev.includes(tag.name)
                                  ? prev.filter(t => t !== tag.name)
                                  : [...prev, tag.name]
                              )}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100 ${
                                active
                                  ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]'
                                  : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]'
                              }`}
                            >
                              <div className="h-7 w-7 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center flex-shrink-0">
                                <TagIcon className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p
                                  className="text-foreground dark:text-[#f7f8f8] truncate"
                                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                                >
                                  {tag.name}
                                </p>
                                <p
                                  className="text-muted-foreground dark:text-[#8a8f98]"
                                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                                >
                                  {tag.count} contact{tag.count === 1 ? '' : 's'}
                                </p>
                              </div>
                              {active && (
                                <CheckCircle className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Segmented tabs */}
            <div
              className="inline-flex p-1 gap-1 rounded-md bg-muted/40 border border-border/50 dark:bg-white/[0.03] dark:border-white/[0.06]"
              role="tablist"
            >
              {[
                { key: 'search' as const, label: 'People search', icon: MagnifyingGlass },
                { key: 'lists' as const,  label: 'Saved lists',   icon: ListBullets },
              ].map(t => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={activeTab === t.key}
                  onClick={() => { setActiveTab(t.key); setSelectedPeople(new Set()); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded border transition-colors duration-150 ${
                    activeTab === t.key
                      ? 'bg-background text-foreground border-border/60 shadow-sm dark:bg-white/[0.06] dark:text-[#f7f8f8] dark:border-white/[0.10] dark:shadow-none'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-background/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.03]'
                  }`}
                  style={{ fontSize: '12px', fontWeight: activeTab === t.key ? 590 : 510, letterSpacing: '-0.13px' }}
                >
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              ))}
            </div>

            <button
              onClick={handleClose}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
              aria-label="Close Apollo search"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 flex overflow-hidden">
          {/* ── SEARCH TAB ── */}
          {activeTab === 'search' && (
            <>
              {/* Left sidebar: filters */}
              <div className="w-80 border-r border-border/50 bg-card dark:bg-[#191a1b]/40 dark:border-white/[0.06] p-5 overflow-y-auto shrink-0 space-y-5">
                <div className="flex items-center justify-between">
                  <h2
                    className="text-foreground dark:text-[#f7f8f8] inline-flex items-center gap-2"
                    style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.182px' }}
                  >
                    <Funnel className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
                    Filters
                  </h2>
                  {hasActiveFilters && (
                    <button
                      onClick={resetFilters}
                      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors"
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      <ArrowCounterClockwise className="h-3 w-3" /> Reset
                    </button>
                  )}
                </div>

                {/* Keywords */}
                <div>
                  <label className={FIELD_LABEL}>Keywords</label>
                  <div className="relative">
                    <MagnifyingGlass className={FIELD_ICON} />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSearchDisplayPage(1); handleSearch(); } }}
                      placeholder="e.g. healthcare, SaaS…"
                      className={FIELD_INPUT_WITH_ICON}
                    />
                  </div>
                </div>

                {/* Job Titles */}
                <div>
                  <label className={FIELD_LABEL}>Job titles</label>
                  <div className="relative">
                    <Briefcase className={FIELD_ICON} />
                    <input
                      type="text"
                      value={titles}
                      onChange={(e) => setTitles(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSearchDisplayPage(1); handleSearch(); } }}
                      placeholder="CEO, VP Sales, Director…"
                      className={FIELD_INPUT_WITH_ICON}
                    />
                  </div>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={includeSimilarTitles}
                      onChange={(e) => setIncludeSimilarTitles(e.target.checked)}
                      className="rounded-sm h-3.5 w-3.5 border-border/60 dark:border-white/[0.12] accent-[#5e6ad2]"
                    />
                    <span
                      className="text-muted-foreground group-hover:text-foreground dark:text-[#8a8f98] dark:group-hover:text-[#f7f8f8] transition-colors"
                      style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                    >
                      Include similar titles
                    </span>
                  </label>
                </div>

                {/* Companies (Apollo autocomplete) */}
                <CompanyAutocomplete
                  selectedCompanies={selectedCompanies}
                  onAdd={(org) => {
                    if (!selectedCompanies.find((c) => c.id === org.id)) {
                      setSelectedCompanies((prev) => [...prev, org]);
                    }
                  }}
                  onRemove={(id) => setSelectedCompanies((prev) => prev.filter((c) => c.id !== id))}
                />

                {/* Company Domains */}
                <div>
                  <label className={FIELD_LABEL}>Company domains</label>
                  <div className="relative">
                    <Globe className={FIELD_ICON} />
                    <input
                      type="text"
                      value={companyDomains}
                      onChange={(e) => setCompanyDomains(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSearchDisplayPage(1); handleSearch(); } }}
                      placeholder="apollo.io, microsoft.com…"
                      className={FIELD_INPUT_WITH_ICON}
                    />
                  </div>
                  <p
                    className="text-muted-foreground/80 dark:text-[#62666d] mt-1"
                    style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                  >
                    Comma-separated, no www.
                  </p>
                </div>

                {/* Person Location */}
                <div>
                  <label className={FIELD_LABEL}>Person location</label>
                  <div className="relative">
                    <MapPin className={FIELD_ICON} />
                    <input
                      type="text"
                      value={locations}
                      onChange={(e) => setLocations(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSearchDisplayPage(1); handleSearch(); } }}
                      placeholder="California, Ireland, Chicago…"
                      className={FIELD_INPUT_WITH_ICON}
                    />
                  </div>
                </div>

                {/* Company HQ Location */}
                <div>
                  <label className={FIELD_LABEL}>Company HQ location</label>
                  <div className="relative">
                    <Buildings className={FIELD_ICON} />
                    <input
                      type="text"
                      value={orgLocations}
                      onChange={(e) => setOrgLocations(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSearchDisplayPage(1); handleSearch(); } }}
                      placeholder="Texas, Tokyo, Spain…"
                      className={FIELD_INPUT_WITH_ICON}
                    />
                  </div>
                </div>

                {/* Seniority */}
                <div>
                  <label className={FIELD_LABEL}>Seniority</label>
                  <div className="flex flex-wrap gap-1">
                    {SENIORITY_OPTIONS.map((opt) => {
                      const on = seniority.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setSeniority((prev) =>
                            prev.includes(opt.value) ? prev.filter((s) => s !== opt.value) : [...prev, opt.value]
                          )}
                          className={`${PILL_BASE} ${on ? PILL_ACTIVE : PILL_INACTIVE}`}
                          style={PILL_TEXT_STYLE}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Employee Count */}
                <div>
                  <label className={FIELD_LABEL}>Company size</label>
                  <div className="flex flex-wrap gap-1">
                    {EMPLOYEE_RANGE_OPTIONS.map((opt) => {
                      const on = employeeRanges.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setEmployeeRanges((prev) =>
                            prev.includes(opt.value) ? prev.filter((r) => r !== opt.value) : [...prev, opt.value]
                          )}
                          className={`${PILL_BASE} ${on ? PILL_ACTIVE : PILL_INACTIVE}`}
                          style={PILL_TEXT_STYLE}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Advanced toggle */}
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center justify-between text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors py-1"
                  style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  <span className="uppercase tracking-[0.06em]">Advanced filters</span>
                  {showAdvanced ? <CaretUp className="h-3.5 w-3.5" /> : <CaretDown className="h-3.5 w-3.5" />}
                </button>

                {showAdvanced && (
                  <div className="space-y-5 pt-1">
                    {/* Revenue Range */}
                    <div>
                      <label className={FIELD_LABEL}>Revenue range (USD)</label>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <CurrencyDollar className={FIELD_ICON} />
                          <input
                            type="text"
                            value={revenueMin}
                            onChange={(e) => setRevenueMin(e.target.value)}
                            placeholder="Min"
                            className={FIELD_INPUT_WITH_ICON}
                          />
                        </div>
                        <span
                          className="text-muted-foreground dark:text-[#62666d] tabular-nums"
                          style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                        >
                          to
                        </span>
                        <div className="relative flex-1">
                          <CurrencyDollar className={FIELD_ICON} />
                          <input
                            type="text"
                            value={revenueMax}
                            onChange={(e) => setRevenueMax(e.target.value)}
                            placeholder="Max"
                            className={FIELD_INPUT_WITH_ICON}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Technologies */}
                    <div>
                      <label className={FIELD_LABEL}>Technologies used</label>
                      <div className="relative">
                        <Monitor className={FIELD_ICON} />
                        <input
                          type="text"
                          value={technologies}
                          onChange={(e) => setTechnologies(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { setSearchDisplayPage(1); handleSearch(); } }}
                          placeholder="Salesforce, HubSpot, WordPress…"
                          className={FIELD_INPUT_WITH_ICON}
                        />
                      </div>
                      <p
                        className="text-muted-foreground/80 dark:text-[#62666d] mt-1"
                        style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                      >
                        Comma-separated. Matches any.
                      </p>
                    </div>

                    {/* Email Status */}
                    <div>
                      <label className={FIELD_LABEL}>Email status</label>
                      <div className="flex flex-wrap gap-1">
                        {EMAIL_STATUS_OPTIONS.map((opt) => {
                          const on = emailStatus.includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setEmailStatus((prev) =>
                                prev.includes(opt.value) ? prev.filter((s) => s !== opt.value) : [...prev, opt.value]
                              )}
                              className={`${PILL_BASE} ${on ? PILL_ACTIVE : PILL_INACTIVE}`}
                              style={PILL_TEXT_STYLE}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search button — doubles as the cancel control. While a search
                    is in flight it becomes "Cancel Request"; clicking it drops
                    the browser request and asks the server to stop its sweep,
                    reverting once that reply lands (confirmed or not — see
                    handleCancelSearch). A fresh search resets the client-side
                    display page so results land on the first 25-row window. */}
                <Button
                  variant={apolloLoading ? 'outline' : 'default'}
                  onClick={() => {
                    if (apolloLoading) { handleCancelSearch(); }
                    else { setSearchDisplayPage(1); handleSearch(); }
                  }}
                  disabled={canceling}
                  icon={
                    canceling
                      ? <CircleNotch className="h-4 w-4 animate-spin" />
                      : apolloLoading
                        ? <X className="h-4 w-4" />
                        : <MagnifyingGlass className="h-4 w-4" />
                  }
                  className="w-full"
                >
                  {canceling ? 'Canceling…' : apolloLoading ? 'Cancel Request' : 'Search Apollo'}
                </Button>
              </div>

              {/* Right: results */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* A cancel the server never confirmed drops apolloLoading back
                    to false, which would otherwise leave this pane looking like
                    a clean idle state. Keep a visible trace until the next
                    search replaces it — the toast is easy to miss, and the user
                    may still be paying for a sweep that never stopped. */}
                {unverifiedCancel && !apolloLoading && (
                  <div className="mx-6 mt-4 shrink-0 flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/[0.05] px-3 py-2 dark:border-amber-500/25 dark:bg-amber-500/[0.08]">
                    <Warning className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
                    <p
                      className="text-amber-700 dark:text-amber-300"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {UNVERIFIED_CANCEL_COPY[unverifiedCancel]}
                    </p>
                  </div>
                )}
                {apolloLoading ? (
                  <div className="flex-1 flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-7 w-7 border-2 border-muted-foreground/20 border-t-[#5e6ad2] dark:border-white/[0.08] dark:border-t-[#7170ff]" />
                    <span
                      className="text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                    >
                      Searching Apollo…
                    </span>
                  </div>
                ) : !hasSearched ? (
                  <div className="flex-1 flex items-center justify-center px-6">
                    <div className="text-center max-w-md">
                      <div className="h-12 w-12 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center mx-auto mb-4">
                        <Users className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8] mb-2"
                        style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                      >
                        Find your next prospects
                      </h3>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                      >
                        Use the filters on the left to search Apollo's database. Start with a company or job title to find the right people.
                      </p>
                    </div>
                  </div>
                ) : apolloResults.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center px-6">
                    <div className="text-center max-w-md">
                      <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                        <MagnifyingGlass className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
                      </div>
                      <h3
                        className="text-foreground dark:text-[#f7f8f8] mb-2"
                        style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                      >
                        No results found
                      </h3>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                      >
                        Try adjusting your filters or search terms.
                      </p>
                    </div>
                  </div>
                ) : (
                  <ResultsTable
                    people={displayedPeople}
                    selectionOffset={displayOffset}
                    selectedPeople={selectedPeople}
                    toggleSelect={toggleSelect}
                    toggleSelectAll={toggleSelectAll}
                    onSelectAllResults={selectAllResults}
                    onClearSelection={clearSelection}
                    importing={importing}
                    enriching={enriching}
                    onImport={handleImport}
                    onRevealEmails={handleRevealEmails}
                    onRevealSingle={handleRevealSingle}
                    revealingIds={revealingIds}
                    currentTotal={effectiveTotal}
                    loading={currentLoading}
                    globalRowStart={globalRowStart}
                    globalRowEnd={globalRowEnd}
                    selectedWithEmail={selectedWithEmailCount}
                    selectedWithoutEmail={selectedWithoutEmailCount}
                    onPrev={goToPrevPage}
                    onNext={goToNextPage}
                    disablePrev={atFirstWindow}
                    disableNext={atLastWindow}
                  />
                )}
              </div>
            </>
          )}

          {/* ── LISTS TAB ── */}
          {activeTab === 'lists' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {!selectedList ? (
                <div className="flex-1 overflow-y-auto p-6">
                  {listsLoading ? (
                    <div className="flex items-center justify-center py-20 gap-3">
                      <div className="animate-spin rounded-full h-7 w-7 border-2 border-muted-foreground/20 border-t-[#5e6ad2] dark:border-white/[0.08] dark:border-t-[#7170ff]" />
                      <span
                        className="text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                      >
                        Loading Apollo lists…
                      </span>
                    </div>
                  ) : listsError ? (
                    <div className="flex items-center justify-center py-20 px-6">
                      <div className="text-center max-w-md">
                        <div className="h-12 w-12 rounded-md bg-red-500/10 dark:bg-red-500/[0.10] flex items-center justify-center mx-auto mb-4">
                          <ListBullets className="h-5 w-5 text-red-600 dark:text-red-400" />
                        </div>
                        <h3
                          className="text-foreground dark:text-[#f7f8f8] mb-2"
                          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                        >
                          Could not load lists
                        </h3>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                        >
                          {listsError}
                        </p>
                        <Button variant="outline" size="sm" className="mt-4" onClick={fetchLists}>Retry</Button>
                      </div>
                    </div>
                  ) : apolloLists.length === 0 ? (
                    <div className="flex items-center justify-center py-20 px-6">
                      <div className="text-center max-w-md">
                        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                          <ListBullets className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
                        </div>
                        <h3
                          className="text-foreground dark:text-[#f7f8f8] mb-2"
                          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
                        >
                          No saved lists
                        </h3>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                        >
                          Create lists in Apollo.io to import them here.
                        </p>
                        <Button variant="outline" size="sm" className="mt-4" onClick={fetchLists}>Refresh</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-7xl mx-auto">
                      <div className="flex items-center justify-between mb-5">
                        <h2
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px' }}
                        >
                          Your Apollo lists
                        </h2>
                        <Button variant="outline" size="sm" onClick={fetchLists}>Refresh</Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {apolloLists.map((list) => (
                          <button
                            key={list.id}
                            onClick={() => loadListContacts(list)}
                            className="text-left p-4 rounded-lg border border-border/50 bg-card hover:border-border/80 hover:bg-muted/40 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-[#7170ff]/25 transition-colors duration-150 flex items-center justify-between gap-3 group"
                          >
                            <div className="min-w-0">
                              <div
                                className="text-foreground dark:text-[#f7f8f8] group-hover:text-[#5e6ad2] dark:group-hover:text-[#a5b4fc] transition-colors truncate"
                                style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                {list.name}
                              </div>
                              {list.cached_count != null && (
                                <div
                                  className="text-muted-foreground dark:text-[#8a8f98] tabular-nums mt-1"
                                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                                >
                                  {list.cached_count.toLocaleString()} contacts
                                </div>
                              )}
                            </div>
                            <CaretRight className="h-4 w-4 text-muted-foreground/60 group-hover:text-[#5e6ad2] dark:text-[#62666d] dark:group-hover:text-[#a5b4fc] transition-colors flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* List contacts view */
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="px-6 pt-4 pb-3 shrink-0">
                    <button
                      onClick={() => { setSelectedList(null); setListContacts([]); setSelectedPeople(new Set()); }}
                      className="inline-flex items-center gap-1 text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c5cbff] underline-offset-2 hover:underline transition-colors mb-2"
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Back to lists
                    </button>
                    <h2
                      className="text-foreground dark:text-[#f7f8f8] flex items-baseline gap-2 flex-wrap"
                      style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px' }}
                    >
                      {selectedList.name}
                      <span
                        className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '12px', fontWeight: 400, letterSpacing: '-0.1px' }}
                      >
                        {listTotal.toLocaleString()} contacts
                      </span>
                    </h2>
                  </div>
                  {listLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-7 w-7 border-2 border-muted-foreground/20 border-t-[#5e6ad2] dark:border-white/[0.08] dark:border-t-[#7170ff]" />
                    </div>
                  ) : listContacts.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                      >
                        No contacts in this list.
                      </p>
                    </div>
                  ) : (
                    <ResultsTable
                      people={displayedPeople}
                      selectionOffset={displayOffset}
                      selectedPeople={selectedPeople}
                      toggleSelect={toggleSelect}
                      toggleSelectAll={toggleSelectAll}
                      onSelectAllResults={selectAllResults}
                      onClearSelection={clearSelection}
                      importing={importing}
                      enriching={enriching}
                      onImport={handleImport}
                      onRevealEmails={handleRevealEmails}
                      onRevealSingle={handleRevealSingle}
                      revealingIds={revealingIds}
                      currentTotal={effectiveTotal}
                      loading={listLoading}
                      globalRowStart={globalRowStart}
                      globalRowEnd={globalRowEnd}
                      selectedWithEmail={selectedWithEmailCount}
                      selectedWithoutEmail={selectedWithoutEmailCount}
                      onPrev={goToPrevPage}
                      onNext={goToNextPage}
                      disablePrev={atFirstWindow}
                      disableNext={atLastWindow}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  ), document.body);
}

// ─── Results Table (shared) ─────────────────────────────────────────────────
function ResultsTable({
  people, selectionOffset, selectedPeople, toggleSelect, toggleSelectAll,
  onSelectAllResults, onClearSelection,
  importing, enriching, onImport, onRevealEmails,
  onRevealSingle, revealingIds,
  currentTotal, loading,
  globalRowStart, globalRowEnd,
  selectedWithEmail, selectedWithoutEmail,
  onPrev, onNext, disablePrev, disableNext,
}: {
  people: any[];
  // First-row index of `people` inside the underlying selectedPeople set.
  // Lets the table render a 25-row slice while selection stays keyed to the full batch.
  selectionOffset: number;
  selectedPeople: Set<number>;
  toggleSelect: (idx: number) => void;
  toggleSelectAll: () => void;
  onSelectAllResults: () => void;
  onClearSelection: () => void;
  importing: boolean;
  enriching: boolean;
  onImport: () => void;
  onRevealEmails: () => void;
  onRevealSingle: (personId: string) => void;
  revealingIds: Set<string>;
  currentTotal: number;
  loading: boolean;
  globalRowStart: number;
  globalRowEnd: number;
  // Email counts are computed against the full fetched batch in the parent —
  // so selections on other display pages of the same fetch still count here.
  selectedWithEmail: number;
  selectedWithoutEmail: number;
  onPrev: () => void;
  onNext: () => void;
  disablePrev: boolean;
  disableNext: boolean;
}) {
  const importDisabled = importing || selectedWithEmail === 0;
  const importHint =
    selectedWithEmail === 0
      ? 'Reveal emails on at least one selected contact first'
      : selectedWithoutEmail > 0
        ? `${selectedWithoutEmail} contact${selectedWithoutEmail === 1 ? '' : 's'} without a revealed email will be skipped`
        : undefined;
  // Header checkbox reflects only the rows visible on this display page.
  const allVisibleSelected =
    people.length > 0 && people.every((_, i) => selectedPeople.has(i + selectionOffset));
  const someVisibleSelected =
    people.some((_, i) => selectedPeople.has(i + selectionOffset));
  // Partial page selection → indeterminate dash, not a full check.
  const headerIndeterminate = someVisibleSelected && !allVisibleSelected;

  // Two-tier select-all (Gmail/Airtable pattern): the header checkbox scopes to
  // the visible page; when the page is fully selected but the fetched set spans
  // more than one page, a banner offers to extend selection to every result.
  const hasMoreThanOnePage = currentTotal > people.length;
  const allResultsSelected = currentTotal > 0 && selectedPeople.size >= currentTotal;
  const showSelectAllBanner =
    hasMoreThanOnePage && (allVisibleSelected || allResultsSelected);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-border/50 dark:border-white/[0.06] bg-card dark:bg-[#191a1b]/40 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {/* Pure status label — the select-all control lives in the table's
              header column (aligned above the row checkboxes), so this count is
              never mistaken for a "select all N" toggle. */}
          <span
            className="text-foreground dark:text-[#f7f8f8] tabular-nums"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            {selectedPeople.size > 0
              ? `${selectedPeople.size} selected`
              : `${currentTotal.toLocaleString()} results`}
          </span>
        </div>
        {selectedPeople.size > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedWithoutEmail > 0 && (
              <Button variant="outline" size="sm" onClick={onRevealEmails} loading={enriching} icon={<LockOpen className="h-4 w-4" />}>
                Reveal {selectedWithoutEmail} email{selectedWithoutEmail > 1 ? 's' : ''}
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => onImport()}
              loading={importing}
              disabled={importDisabled}
              title={importHint}
              icon={<DownloadSimple className="h-4 w-4" />}
            >
              Import {selectedWithEmail} contact{selectedWithEmail === 1 ? '' : 's'}
              {selectedWithoutEmail > 0 && selectedWithEmail > 0 && (
                <span className="ml-1 opacity-70">({selectedWithoutEmail} skipped)</span>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Two-tier select-all banner — bridges "this page" and "all results". */}
      {showSelectAllBanner && (
        <div
          className="flex items-center justify-center gap-1.5 px-6 py-2 border-b border-border/50 bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08] dark:border-white/[0.06]"
          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
        >
          <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums">
            {allResultsSelected
              ? `All ${currentTotal.toLocaleString()} result${currentTotal === 1 ? '' : 's'} selected.`
              : `All ${people.length} on this page selected.`}
          </span>
          <button
            type="button"
            onClick={allResultsSelected ? onClearSelection : onSelectAllResults}
            className="text-[#5e6ad2] hover:text-[#4e5ac0] dark:text-[#a5b4fc] dark:hover:text-[#c4cdff] transition-colors duration-150"
            style={{ fontWeight: 510 }}
          >
            {allResultsSelected
              ? 'Clear selection'
              : `Select all ${currentTotal.toLocaleString()} results`}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-muted/40 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10 dark:bg-[#191a1b]/80 dark:backdrop-blur dark:border-white/[0.06]">
            <tr>
              <th className="px-6 py-3 text-left w-10">
                {/* Select-all lives in the checkbox column, aligned above the
                    row checkboxes — the universal "toggle the rows in view"
                    affordance. Scoped to the current page; the banner above
                    extends it to every result. */}
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = headerIndeterminate; }}
                  onChange={toggleSelectAll}
                  className="rounded-sm h-4 w-4 border-border/60 dark:border-white/[0.12] accent-[#5e6ad2] align-middle"
                  aria-label="Select all on this page"
                />
              </th>
              {(['Name','Title','Company','Location','Email'] as const).map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-muted-foreground dark:text-[#8a8f98] uppercase"
                  style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                >
                  {h}
                </th>
              ))}
              <th
                className="px-4 py-3 text-right text-muted-foreground dark:text-[#8a8f98] uppercase w-20"
                style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
              >
                Links
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
            {people.map((person, idx) => {
              const globalIdx = idx + selectionOffset;
              return (
              <tr
                key={person.id ?? globalIdx}
                className={`transition-colors duration-150 ${
                  selectedPeople.has(globalIdx)
                    ? 'bg-[#5e6ad2]/[0.05] dark:bg-[#7170ff]/[0.06]'
                    : 'hover:bg-muted/40 dark:hover:bg-white/[0.03]'
                }`}
              >
                <td className="px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedPeople.has(globalIdx)}
                    onChange={() => toggleSelect(globalIdx)}
                    className="rounded-sm h-4 w-4 border-border/60 dark:border-white/[0.12] accent-[#5e6ad2]"
                    aria-label={`Select ${person.first_name ?? ''} ${person.last_name ?? ''}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <div
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    {person.first_name} {person.last_name}
                  </div>
                  {person.seniority && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground border border-border/50 mt-0.5 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]"
                      style={{ fontSize: '10px', fontWeight: 510, letterSpacing: '-0.05px' }}
                    >
                      {person.seniority}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div
                    className="text-foreground dark:text-[#f7f8f8] max-w-[200px] truncate"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    {person.title || <span className="text-muted-foreground/40 dark:text-[#3e3e44]">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div
                    className="text-foreground dark:text-[#f7f8f8] truncate"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    {person.organization?.name || person.organization_name || <span className="text-muted-foreground/40 dark:text-[#3e3e44] font-normal">—</span>}
                  </div>
                  {person.organization?.industry && (
                    <div
                      className="text-muted-foreground dark:text-[#8a8f98] truncate"
                      style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                    >
                      {person.organization.industry}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    {[person.city, person.state, person.country].filter(Boolean).join(', ') || (
                      <span className="text-muted-foreground/40 dark:text-[#3e3e44]">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {person.email ? (
                    <span className="flex items-center gap-1.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      <Envelope className="h-3 w-3 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <span className="truncate max-w-[180px] text-foreground dark:text-[#f7f8f8]">{person.email}</span>
                    </span>
                  ) : person.id && revealingIds.has(person.id) ? (
                    <span
                      className="flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      <CircleNotch className="h-3 w-3 animate-spin shrink-0" />
                      <span>Revealing…</span>
                    </span>
                  ) : person.email_not_found ? (
                    <span
                      className="flex items-center gap-1.5 text-muted-foreground/50 dark:text-[#62666d]"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      title="Apollo has no email for this person"
                    >
                      <Envelope className="h-3 w-3 shrink-0" />
                      <span>No email</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => person.id && onRevealSingle(person.id)}
                      disabled={!person.id}
                      title="Reveal email (uses 1 Apollo credit)"
                      className="inline-flex items-center gap-1.5 px-1.5 py-0.5 -mx-1.5 rounded text-[#5e6ad2] hover:bg-[#5e6ad2]/10 dark:text-[#a5b4fc] dark:hover:bg-[#7170ff]/[0.10] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px', fontWeight: 510 }}
                    >
                      <LockOpen className="h-3 w-3 shrink-0" />
                      <span>Click to reveal</span>
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {person.linkedin_url && (
                      <a
                        href={person.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-[#0A66C2] hover:bg-[#0A66C2]/10 dark:text-[#8a8f98] dark:hover:text-[#5e9ad8] dark:hover:bg-[#0A66C2]/[0.12] transition-colors"
                        aria-label="Open LinkedIn"
                      >
                        <LinkedinLogo weight="fill" className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {person.organization?.website_url && (
                      <a
                        href={person.organization.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors"
                        aria-label="Open website"
                      >
                        <Globe className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination — 25 rows per visual page. When you hit the edge of the loaded
          server batch, Prev/Next auto-fetches the previous/next 100-row batch.
          Always shown (even on a single page) so the result count and controls
          stay visible; the buttons just disable when there's nowhere to go. */}
      <div className="flex items-center justify-between px-6 py-3 border-t border-border/50 bg-card dark:bg-[#191a1b]/40 dark:border-white/[0.06] shrink-0">
        <p
          className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
        >
          Showing{' '}
          <span className="text-foreground dark:text-[#f7f8f8] font-medium">{globalRowStart.toLocaleString()}</span>
          {globalRowEnd > globalRowStart && (
            <>
              –<span className="text-foreground dark:text-[#f7f8f8] font-medium">{globalRowEnd.toLocaleString()}</span>
            </>
          )}{' '}
          of <span className="text-foreground dark:text-[#f7f8f8] font-medium">{currentTotal.toLocaleString()}</span>
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={disablePrev || loading} onClick={onPrev}>
            <CaretLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button variant="outline" size="sm" disabled={disableNext || loading} onClick={onNext}>
            Next <CaretRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
